import { useHooks } from '@everlywell/leaves';
import {
  Accordion,
  Drawer,
  DrawerBody,
  DrawerCloseButton,
  DrawerContent,
  DrawerFooter,
  DrawerHeader,
  DrawerOverlay,
  Heading,
} from '@everlywell/ui-kit';
import { useCart } from 'components/Cart';
import { useFlags } from 'gatsby-plugin-launchdarkly';
import { isEmpty } from 'lodash-es';
import React, { useEffect, useState } from 'react';
import analytics from 'utils/analytics';
import { ANALYTICS } from 'utils/constants/analytics';
import {
  ELIMINATION_DIET_COACHING_SLUG,
  VITAMINS,
} from 'utils/constants/products';
import { logError } from 'utils/helpers';
import { findActivePromotionForProduct } from 'utils/helpers/activePromoHelpers';
import useAdvertisedPromos from 'utils/hooks/useAdvertisedPromos';
import { SubscriptionVariantType } from 'utils/types';

import { usePurchaseOptionsContext } from '../../PurchaseOptionsDrawerProvider.context';
import PreCartFooter from '../PreCartFooter';
import PurchaseOption, {
  PurchaseOptionDescription,
  PurchaseOptionHeading,
} from '../PurchaseOption';
import SubscribeAndSaveOption, {
  SubscribeAndSaveOptionProps,
} from '../SubscribeAndSaveOption';
import {
  ELIMINATION_DIET_DESCRIPTION,
  TEST_KIT_DESCRIPTION,
  VITAMIN_DESCRIPTION,
} from './PurchaseOptionsDrawer.contants';

export enum PurchaseOptionType {
  OneTime = 'one-time-purchase',
  SubscribeAndSave = 'subscribe-and-save',
  EverlywellPlusMembership = 'everlywell-plus-membership',
  MonthlyMembership = 'everlywell-plus-membership-1-month',
  YearlyMembership = 'everlywell-plus-membership-1-year',
  WeightCareMembership = 'weight-management-membership',
}

export enum MembershipOptionType {
  MonthlyMembership = PurchaseOptionType.MonthlyMembership,
  YearlyMembership = PurchaseOptionType.YearlyMembership,
  WeightCareMembership = PurchaseOptionType.WeightCareMembership,
}

export type PurchaseOptionsDrawerProps = {};

/**
 * Purchase Options Drawer component
 * used to display the purchase options for a product in Mobile view
 */
function PurchaseOptionsDrawer(props: PurchaseOptionsDrawerProps): JSX.Element {
  const { product, clearProduct } = usePurchaseOptionsContext();

  const { addItemToCartAsync, isAddItemToCartLoading } = useCart({
    addItemToCart: {
      // prevent the default behavior of opening the cart
      onError: () => Promise.resolve(false),
    },
  });

  const { advertisedPromos } = useAdvertisedPromos();
  const [accordionIndex, setAccordionIndex] = useState(0);
  const [purchaseOptionId, setPurchaseOptionId] =
    useState<PurchaseOptionType | null>(null);
  const [selectedSubscriptionVariant, setSelectedSubscriptionVariant] =
    useState<SubscriptionVariantType | null>(null);
  const purchaseOptionPrice = selectedSubscriptionVariant
    ? selectedSubscriptionVariant.price
    : product?.price;

  const isOverlayOpen = !isEmpty(product);
  const hasMembershipCheckout = useFlags().preCartToMembershipCheckout ?? false;

  const isVitamin = VITAMINS.includes(product?.slug ?? '');

  const isEliminationDiet = product?.slug === ELIMINATION_DIET_COACHING_SLUG;

  const oneTimePurchaseDesription = isEliminationDiet
    ? ELIMINATION_DIET_DESCRIPTION
    : isVitamin
    ? VITAMIN_DESCRIPTION
    : TEST_KIT_DESCRIPTION;

  const activePromo =
    findActivePromotionForProduct(product?.productId ?? 0, advertisedPromos) ||
    null;

  // reset the state when the overlay is closed
  useEffect(() => {
    if (!isOverlayOpen) {
      setAccordionIndex(0);
      setPurchaseOptionId(null);
      setSelectedSubscriptionVariant(null);
    }
  }, [isOverlayOpen]);

  // initial state for the Subscribe and Save option when the product has a preselected subscription = true
  const shouldPreselectSubscribeAndSave =
    Boolean(product?.preSelectedSubscription) &&
    !product?.hideSubscription &&
    !isEmpty(product?.subscriptionVariants);

  useEffect(() => {
    if (
      isOverlayOpen &&
      purchaseOptionId === null &&
      shouldPreselectSubscribeAndSave
    ) {
      setAccordionIndex(1);
    }
  }, [isOverlayOpen, purchaseOptionId, shouldPreselectSubscribeAndSave]);

  // disbable scroll when the overlay is open
  useHooks.useDisableScroll(isOverlayOpen);

  useEffect(() => {
    if (isOverlayOpen) {
      analytics.track({
        event: ANALYTICS.EVENTS.VIEWED_COMPONENT,
        data: {
          label: 'Pre-Cart',
          experience: 'Pre-Cart',
          version: hasMembershipCheckout ? 'v2' : 'v1',
        },
      });
    }
  }, [isOverlayOpen, hasMembershipCheckout]);

  // track purchase option change
  useEffect(() => {
    if (purchaseOptionId && product) {
      analytics.track({
        event: ANALYTICS.EVENTS.CLICKED_BUTTON,
        data: {
          label: 'Pre-Cart - Purchase Option',
          optionId: purchaseOptionId as string,
          experience: 'Pre-Cart',
          'Product Name': product.name,
        },
      });
    }
  }, [product, purchaseOptionId]);

  // Add a variant to the cart
  const handleAddToCart = async ({ variantId }: { variantId: number }) => {
    try {
      await addItemToCartAsync({
        variant_id: variantId,
        quantity: 1,
      });

      clearProduct();
    } catch (error) {
      logError('There was an issue adding the item to the cart', {
        component: 'PurchaseOptionsDrawer',
        method: 'handleAddToCart',
        experience: 'Pre-Cart',
      });
    }
  };

  const handleAccordionChange = (index: number) => {
    if (typeof index === 'number') {
      setAccordionIndex(index);
    }
  };

  const getSelectedOption = () => {
    return purchaseOptionId || 'No option';
  };

  const handleSubscriptionVariantChange: SubscribeAndSaveOptionProps['onSubscriptionVariantChange'] =
    (variant) => {
      setSelectedSubscriptionVariant(variant);
    };
  const handleCTAClick = (ctaText: string) => {
    let optionId = getSelectedOption();

    analytics.track({
      event: ANALYTICS.EVENTS.CLICKED_BUTTON,
      data: {
        label: `Pre-Cart - ${ctaText}`,
        'Product Name': product?.name,
        experience: 'Pre-Cart',
        optionId: optionId,
      },
    });

    if (!isEliminationDiet && !isVitamin) {
      const variantId = selectedSubscriptionVariant?.id ?? product?.variantId;
      if (variantId) {
        handleAddToCart({
          variantId,
        });
      }
    }
  };

  const trackDrawerClose = () => {
    analytics.track({
      event: ANALYTICS.EVENTS.CLICKED_BUTTON,
      data: {
        label: 'Pre Cart Close',
        experience: 'Pre-Cart',
        optionId: purchaseOptionId as string,
      },
    });
  };

  return (
    <Drawer
      isOpen={isOverlayOpen}
      placement={'bottom'}
      onClose={() => {
        clearProduct();
        trackDrawerClose();
      }}
      blockScrollOnMount={false} // disable block scroll from chakra due to this bug: https://github.com/chakra-ui/chakra-ui/issues/8261
    >
      {/* NOTE: zIndex index is overrode in order to place Cart on top of the Chat Bubble */}
      <DrawerOverlay zIndex={9997} />
      <DrawerContent
        position="relative"
        gap={0}
        containerProps={{ zIndex: 9998 }}
        minHeight="90vh"
        top="auto !important"
        borderRadius="24px 24px 0 0"
      >
        <DrawerHeader paddingX={[4, 8]} paddingBottom={4}>
          <Heading as="h1" size="md">
            Select options
          </Heading>
          <DrawerCloseButton />
        </DrawerHeader>

        <DrawerBody paddingY={4} paddingX={[4, 8]} overflow="auto">
          <Accordion
            display="flex"
            flexDirection="column"
            gap={3}
            onChange={handleAccordionChange}
            index={accordionIndex}
            defaultIndex={0}
          >
            <PurchaseOption
              id={PurchaseOptionType.OneTime}
              heading={
                <PurchaseOptionHeading>One-time purchase</PurchaseOptionHeading>
              }
              description={
                <PurchaseOptionDescription>
                  {oneTimePurchaseDesription}
                </PurchaseOptionDescription>
              }
              onSelected={() => {
                setPurchaseOptionId(PurchaseOptionType.OneTime);
                setSelectedSubscriptionVariant(null);
              }}
              price={{
                amount: parseFloat(product?.price ?? '0'),
                unitLabel: '/each',
              }}
            />
            <SubscribeAndSaveOption
              id={PurchaseOptionType.SubscribeAndSave}
              onSelected={(id) => {
                setPurchaseOptionId(id as PurchaseOptionType);
              }}
              product={product}
              onSubscriptionVariantChange={handleSubscriptionVariantChange}
              isExpanded={
                PurchaseOptionType.SubscribeAndSave === purchaseOptionId
              }
            />
          </Accordion>
        </DrawerBody>
        <DrawerFooter
          background="tints.lightCream"
          padding={0}
          display={'flex'}
          flexDirection="column"
          alignItems="stretch"
          position="relative"
        >
          <PreCartFooter
            selectedOption={purchaseOptionId as PurchaseOptionType}
            variant={selectedSubscriptionVariant || undefined}
            price={purchaseOptionPrice ?? ''}
            onClick={handleCTAClick}
            isLoading={isAddItemToCartLoading}
            product={product}
            activePromo={activePromo}
          />
        </DrawerFooter>
      </DrawerContent>
    </Drawer>
  );
}

export default PurchaseOptionsDrawer;

export const useIsPurchaseOptionsEnabled = () => {
  // Pre-cart drawer is disabled since membership option has been removed
  return false;
};
